import { useEffect, useMemo } from 'react';

import { FEATURE_FLAGS } from '@/constants/featureFlags';
import {
  isReferenceChampion,
  parseReferenceChampionConfig,
  type ReferenceChampionConfig,
} from '@/constants/referenceChampions';
import { useFeatureFlag, useFeatureFlagValue } from '@/hooks/useFeatureFlag';
import { Sentry } from '@/util/sentry';

export type ReferenceChampionBadgeData = {
  label: string;
  /** Absent when the payload sets no mark, which renders the label on its own. */
  emoji?: string;
};

/**
 * Payload problems already sent this session, so a directory page does not report the same
 * bad roster once per card. Keyed by the issues themselves rather than by the payload, since
 * an operator fixing one of several mistakes should still surface what is left.
 */
const reportedIssues = new Set<string>();

function reportPayloadIssues(issues: string[]) {
  const signature = issues.join(' | ');

  if (!signature || reportedIssues.has(signature)) {
    return;
  }

  reportedIssues.add(signature);

  Sentry.captureMessage('Reference champion flag payload was partly unusable', {
    level: 'warning',
    tags: { component: 'useReferenceChampionBadge', source: 'flagsmith-payload' },
    extra: { issues },
  });
}

/**
 * Resolves everything the badge needs to render, or null when it should not render.
 *
 * MVP: one flag gates the feature and carries its manually curated roster plus badge copy.
 * The app does not calculate or refresh qualification; operators replace the roster in
 * Flagsmith when the measurement changes.
 *
 * Two payload shapes are accepted. A bare roster of expert id to on or off:
 *
 *     { "836": true, "933": true }
 *
 * Or the same roster with copy alongside it, where either copy field may be omitted:
 *
 *     { "label": "Reference Champion", "emoji": "🏆", "roster": { "836": true } }
 *
 * Without a value nothing renders, since there is deliberately no roster in code: expert
 * ids differ per environment, so a hardcoded default would badge whoever happens to hold
 * that id elsewhere. An explicit empty roster switches every badge off while leaving the
 * flag enabled.
 */
export function useReferenceChampionBadge(
  expertId: string | number
): ReferenceChampionBadgeData | null {
  const isEnabled = useFeatureFlag(FEATURE_FLAGS.EXPERT_REFERENCE_BADGES);

  const payload = useFeatureFlagValue(FEATURE_FLAGS.EXPERT_REFERENCE_BADGES);

  const config: ReferenceChampionConfig = useMemo(
    () => parseReferenceChampionConfig(payload),
    [payload]
  );

  /**
   * Only reported while the flag is on. A payload left half-edited behind a disabled flag is
   * a draft, not an incident, and alerting on it would train the alert to be ignored.
   */
  useEffect(() => {
    if (isEnabled) {
      reportPayloadIssues(config.issues);
    }
  }, [isEnabled, config.issues]);

  if (!isEnabled || !isReferenceChampion(config.roster, expertId)) {
    return null;
  }

  return { label: config.label, emoji: config.emoji };
}
